/**
 * Testi italiani della pagina «Monitoraggio», riservata agli amministratori:
 * Grafana, accesso a Prometheus e Alertmanager, allarmi su Telegram.
 *
 * La pagina parla a chi amministra il cluster, quindi qui compaiono di
 * proposito nomi che altrove la skill `testi-ui-italiano` tiene fuori
 * (`Secret`, `kubectl`, il nome dello script): sono ciò che quella persona deve
 * scrivere o cercare, e una parafrasi la costringerebbe a indovinare.
 */

/** Testi della pagina di monitoraggio. */
export const monitoring = {
  /** Titolo della pagina. */
  title: 'Monitoraggio',
  /** Riga sotto il titolo. */
  description: 'Metriche e cruscotti della piattaforma, e dove arrivano gli allarmi.',

  /** Scheda di Grafana. */
  grafana: {
    /** Titolo della scheda. */
    title: 'Grafana',
    /** Collegamento alla pagina iniziale di Grafana. */
    open: 'Apri Grafana',
    /** Dove sta la password. */
    credentials: "Accedi con l'utente admin. La password è nel Secret <code>{{secret}}</code> del cluster.",
    /** Nome accessibile dell'elenco dei cruscotti. */
    dashboards: 'Cruscotti',
    /** Cruscotto della panoramica. */
    overview: 'Panoramica sistema',
    /** Cruscotto dei set di agenti. */
    agentSet: 'Set di agenti',
    /** Cruscotto di un progetto ospitato. */
    hostedProject: 'Progetto ospitato',
  },

  /** Scheda di Prometheus e Alertmanager. */
  cluster: {
    /** Titolo della scheda. */
    title: 'Prometheus e Alertmanager',
    /** Perché servono i comandi. */
    notExposed: 'Non sono raggiungibili da internet. Apri un tunnel dal tuo computer con kubectl.',
    /** Etichetta del comando di Prometheus. */
    prometheus: 'Prometheus, poi apri {{url}}',
    /** Etichetta del comando di Alertmanager. */
    alertmanager: 'Alertmanager, poi apri {{url}}',
  },

  /** Scheda degli allarmi su Telegram. */
  telegram: {
    /** Titolo della scheda. */
    title: 'Allarmi su Telegram',
    /** Lettura dello stato non riuscita. */
    loadFailed: 'Caricamento dello stato degli allarmi non riuscito. Riprova.',
    /** Spiegazione del 403. */
    forbidden: 'Questa sezione è per gli amministratori della piattaforma.',

    /** Stato attuale. */
    status: {
      /** Nome accessibile del riepilogo. */
      label: 'Stato attuale',
      /** Voce dello stato. */
      state: 'Stato',
      /** Inoltro acceso. */
      on: 'Attivi',
      /** Inoltro spento. */
      off: 'Spenti',
      /** Bot mai collegato. */
      notConfigured: 'Non collegati',
      /** Voce del bot. */
      bot: 'Bot',
      /** Voce della chat. */
      chat: 'Chat',
      /** Voce della provenienza del token. */
      tokenSource: 'Token del bot',
      /** Token salvato da questa pagina. */
      tokenWeb: 'Salvato da questa pagina',
      /** Token letto dalla configurazione del server. */
      tokenEnvironment: 'Dalla configurazione del server',
      /** Voce dell'ultima consegna. */
      lastSent: 'Ultima consegna',
      /** Consegna mai avvenuta. */
      neverSent: 'Nessuna',
      /** Titolo dell'avviso sull'ultimo errore. */
      lastError: 'Ultima consegna non riuscita',
    },

    /** Chiave di cifratura mancante sul server. */
    keyMissing:
      'Manca la chiave di cifratura sul server. Chi amministra il cluster deve eseguire una volta {{command}}.',

    /** Passi per creare il bot. */
    guide: {
      /** Primo passo. */
      botFather: 'Su Telegram apri @BotFather e invia /newbot.',
      /** Secondo passo. */
      token: 'Copia il token che ti dà BotFather e incollalo qui sotto.',
      /** Terzo passo. */
      message: 'Scrivi un messaggio al bot, oppure aggiungilo al gruppo che deve ricevere gli allarmi.',
      /** Quarto passo. */
      chats: 'Premi «Cerca le chat» e scegli quella giusta.',
    },

    /** Form di configurazione. */
    form: {
      /** Nome accessibile del form. */
      label: 'Configurazione degli allarmi su Telegram',
      /** Etichetta del token. */
      token: 'Token del bot',
      /** Segnaposto quando un token è già salvato. */
      tokenKeep: 'Lascia vuoto per tenere quello salvato',
      /** Errore: token mancante e nessuno salvato. */
      tokenRequired: 'Incolla il token del bot.',
      /** Etichetta della chat. */
      chat: 'Chat',
      /** Aiuto sotto la chat. */
      chatHelp: "Scegli una chat trovata o scrivi l'id. Gli id dei gruppi iniziano con -100.",
      /** Errore: chat mancante. */
      chatRequired: 'Scegli una chat o scrivi il suo id.',
      /** Voce di una chat trovata. */
      chatOption: '{{title}} ({{type}}, {{id}})',
      /** Pulsante che cerca le chat. */
      findChats: 'Cerca le chat',
      /** Chat trovate. */
      chatsFound_one: 'Trovata {{count}} chat.',
      /** Chat trovate, plurale. */
      chatsFound_other: 'Trovate {{count}} chat.',
      /** Nessuna chat trovata. */
      noChats: 'Nessuna chat trovata. Scrivi un messaggio al bot e riprova.',
      /** Etichetta dell'interruttore. */
      enabled: 'Allarmi attivi',
      /** Pulsante di invio: quattro parole, testo chiesto dall'utente (deroga dichiarata). */
      submit: 'Salva e invia una prova',
      /** Esito del salvataggio. */
      saved: 'Configurazione salvata. Il messaggio di prova è partito.',
    },

    /** Comandi sulla configurazione salvata. */
    actions: {
      /** Pulsante della prova. */
      test: 'Invia una prova',
      /** Esito della prova. */
      tested: 'Messaggio di prova inviato.',
      /** Pulsante che scollega il bot. */
      disconnect: 'Scollega',
      /** Domanda della conferma. */
      disconnectQuestion: 'Scollegare il bot di Telegram?',
      /** Conseguenza dello scollegamento. */
      disconnectConsequence: 'Gli allarmi smettono di arrivare e il token salvato viene cancellato.',
      /** Esito dello scollegamento. */
      disconnected: 'Bot scollegato.',
    },

    /** Rifiuti del servizio, per codice. */
    errors: {
      /** Telegram ha rifiutato token o chat: segue il motivo scritto da Telegram. */
      rejected: 'Telegram ha rifiutato la richiesta: {{detail}}',
      /** Nessun token scritto e nessuno salvato. */
      tokenMissing: 'Manca il token del bot. Incollalo nel campo del token.',
      /** Allarmi non configurati: la prova non ha una chat. */
      notConfigured: 'Il bot non è ancora collegato. Salva prima token e chat.',
      /** Ricaduta generica. */
      generic: 'Operazione non riuscita. Riprova.',
    },
  },

  /** Scheda dell'agente di sistema per gli allarmi (ADR 0034). */
  systemAgent: {
    /** Titolo della scheda. */
    title: 'Agente di sistema',
    /** Che cosa fa l'agente. */
    intro:
      'Legge gli allarmi del cluster. Da solo chiede solo il riavvio di un set in crash; il resto lo propone qui e sulla chat degli allarmi.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento degli incidenti non riuscito. Riprova.',
    /** Spiegazione del 403. */
    forbidden: 'Questa sezione è per gli amministratori della piattaforma.',
    /** Agente spento. */
    off: "L'agente è spento: gli allarmi arrivano solo su Telegram. Per accenderlo servono il segreto del webhook e SYSTEM_AGENT_ENABLED.",
    /** Etichetta dello stato. */
    state: 'Stato',
    /** Agente acceso. */
    on: 'Acceso',
    /** Agente spento, nel tag. */
    offTag: 'Spento',
    /** Azioni automatiche accese. */
    autoOn: 'Azioni automatiche attive',
    /** Azioni automatiche spente. */
    autoOff: 'Solo proposte',
    /** Nome accessibile del filtro. */
    filter: 'Incidenti da mostrare',
    /** Voci del filtro. */
    filters: {
      /** Da gestire. */
      open: 'Da gestire ({{count}})',
      /** Chiusi. */
      closed: 'Chiusi',
      /** Tutti. */
      all: 'Tutti',
    },
    /** Nessun incidente. */
    empty: 'Nessun incidente.',
    /** Intestazioni della tabella. */
    columns: {
      /** Prima consegna. */
      date: 'Quando',
      /** Allarme. */
      alert: 'Allarme',
      /** Bersaglio. */
      target: 'Riguarda',
      /** Esito. */
      triage: 'Esito',
    },
    /** Esiti. */
    triage: {
      /** Solo registrato. */
      noted: 'Registrato',
      /** Azione fatta. */
      acted: 'Fatto',
      /** Proposta. */
      proposed: 'Proposta',
    },
    /** Allarme risolto. */
    resolved: 'Risolto',
    /** Voci del dettaglio. */
    detail: {
      /** Riassunto della regola. */
      summary: 'Regola',
      /** Diagnosi. */
      diagnosis: 'Diagnosi',
      /** Azione fatta. */
      action: 'Fatto',
      /** Proposta. */
      proposal: 'Proposta',
      /** Stato della proposta. */
      proposalStatus: 'Stato della proposta',
    },
    /** Stati della proposta. */
    proposalStatus: {
      /** Nessuna proposta approvabile. */
      none: 'Da eseguire a mano',
      /** In attesa. */
      pending: 'In attesa',
      /** Approvata. */
      approved: 'Approvata',
      /** Scartata. */
      dismissed: 'Scartata',
      /** Scaduta. */
      expired: "Scaduta: l'allarme si è risolto",
      /** Fallita. */
      failed: 'Azione non riuscita',
    },
    /** Azioni approvabili, come compaiono nella conferma. */
    actions: {
      /** Riavvio del set. */
      restart_set: 'Riavvia il set',
      /** Sospensione del set. */
      suspend_set: 'Sospendi il set',
    },
    /** Pulsante di approvazione. */
    approve: 'Approva',
    /** Nome accessibile del pulsante di approvazione. */
    approveNamed: 'Approva: {{action}} ({{target}})',
    /** Domanda della conferma. */
    approveQuestion: '{{action}}?',
    /** Conseguenza del riavvio. */
    restartConsequence: 'Il pod del set si ricrea adesso, anche se gli agenti stanno lavorando. Il lavoro in corso riprende nel pod nuovo.',
    /** Conseguenza della sospensione. */
    suspendConsequence: 'Gli agenti si fermano. Il set si riattiva dalla sua pagina.',
    /** Esito dell'approvazione riuscita. */
    approved: 'Azione eseguita.',
    /** Esito dell'approvazione fallita: l'incidente resta aperto. */
    approveFailed: "Azione non riuscita. L'esito è registrato: guarda il set e riprova a mano.",
    /** Pulsante di chiusura. */
    close: 'Chiudi',
    /** Nome accessibile del pulsante di chiusura. */
    closeNamed: 'Chiudi {{alert}} ({{target}})',
    /** Esito della chiusura. */
    closed: 'Incidente chiuso.',
  },
};

/** Forma dei testi del monitoraggio, usata per tipare le altre lingue. */
export type Monitoring = typeof monitoring;
